import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Home, MessageSquareText, Phone, PlayCircle, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../../components/ui/dialog";

const PEOPLE = [
  { id: "walter", name: "Walter Brandt", initials: "WB", group: "Homebound", reason: "No visit in 34 days", detail: "Homebound rhythm is every 7 days",
    story: [["Oct 2", "Visit", "Brought communion. Walter shared he has been lonely since Ruth passed."], ["Sep 18", "Call", "Asked for prayer for his daughter's move to Denver."]] },
  { id: "harold", name: "Harold Pettersen", initials: "HP", group: "Seniors", reason: "Home from hip surgery", detail: "Follow-up due today",
    story: [["Oct 20", "Call", "Surgery went well. Recovery will take about six weeks."], ["Oct 14", "Need", "Hip surgery scheduled. Meals requested for his first week home."]] },
  { id: "ana", name: "Ana Alvarez", initials: "AA", group: "New Members", reason: "New member, not yet connected", detail: "Joined 3 weeks ago",
    story: [["Oct 12", "Note", "Joined after the newcomers lunch. Interested in serving with kids."]] },
];
const TYPES = [[Phone, "Call"], [MessageSquareText, "Text"], [Home, "Visit"]];
const NEXT = ["In 1 week", "In 2 weeks", "In a month", "No follow-up"];
const STEPS = { today: ["1", "See who needs you", "Today shows the people who need your attention. Tap someone to open them."], person: ["2", "Know their story", "Before you reach out, see what they have been walking through."], log: ["3", "Log the care", "Pick how you connected, jot a line, and choose the next step."], story: ["4", "Care keeps moving", "Their Story now shows what happened and what comes next."] };

const Avatar = ({ p }) => <span className="w-10 h-10 rounded-full bg-[color:var(--canvas)] border border-line flex items-center justify-center text-[13px] font-semibold text-[color:var(--moss)] shrink-0">{p.initials}</span>;
const Back = ({ onClick, label }) => <button onClick={onClick} className="flex items-center gap-1 text-[13px] text-ink-soft mb-3" data-testid="tour-back"><ArrowLeft size={14} />{label}</button>;

const Today = ({ people, done, open }) => (
  <div data-testid="tour-today">
    <p className="text-[11px] uppercase tracking-[0.12em] text-ink-faint">Riverside Community Church</p>
    <p className="font-display font-semibold text-[22px] text-ink mt-1">Good evening, Monica.</p>
    <p className="text-[13px] text-ink-soft">{people.length - done.length ? `${people.length - done.length} people need your attention today.` : "Everyone on your list has been cared for."}</p>
    <div className="mt-4 space-y-2.5">
      {people.map((p) => (
        <button key={p.id} onClick={() => open(p.id)} className="w-full text-left rounded-[14px] border border-line bg-surface p-3 flex gap-3 items-center hover:border-[color:var(--moss)] transition-colors" data-testid={`tour-person-${p.id}`}>
          <Avatar p={p} />
          <span className="flex-1 min-w-0"><span className="block font-semibold text-[14px] text-ink">{p.name}</span>
            {done.includes(p.id) ? <span className="flex items-center gap-1 text-[12px] text-[color:var(--moss)]"><Check size={12} />Cared for today</span> : <span className="block text-[12px] text-[color:var(--clay)]">{p.reason}</span>}
          </span>
          <ArrowRight size={16} className="text-ink-faint" />
        </button>
      ))}
    </div>
  </div>
);

const Story = ({ entries }) => (
  <div className="mt-4" data-testid="tour-story">
    <p className="text-[11px] uppercase tracking-[0.12em] text-ink-faint mb-2">Story</p>
    <ol className="border-l border-line ml-1 space-y-3">
      {entries.map(([d, t, n], i) => (
        <li key={i} className="pl-3 relative">
          <span className="absolute -left-[4px] top-1.5 w-2 h-2 rounded-full bg-[color:var(--moss)]" />
          <p className="text-[11px] text-ink-faint">{d} · {t}</p><p className="text-[13px] text-ink leading-snug">{n}</p>
        </li>
      ))}
    </ol>
  </div>
);

const Person = ({ p, onBack, onLog }) => (
  <div data-testid="tour-person">
    <Back onClick={onBack} label="Today" />
    <div className="flex items-center gap-3"><Avatar p={p} /><div><p className="font-display font-semibold text-[19px] text-ink">{p.name}</p><p className="text-[12px] text-ink-soft">{p.group}</p></div></div>
    <div className="mt-3 rounded-[12px] bg-[color:var(--canvas)] p-3"><p className="text-[13px] font-semibold text-[color:var(--clay)]">{p.reason}</p><p className="text-[12px] text-ink-soft">{p.detail}</p></div>
    <Story entries={p.story} />
    <button onClick={onLog} className="btn btn-primary w-full mt-5" data-testid="tour-log-care">Log care</button>
  </div>
);

const LogCare = ({ p, onBack, onSave }) => {
  const [type, setType] = useState("Call");
  const [note, setNote] = useState("");
  const [next, setNext] = useState(NEXT[0]);
  return (
    <div data-testid="tour-log">
      <Back onClick={onBack} label={p.name} />
      <p className="font-display font-semibold text-[19px] text-ink">Log care for {p.name.split(" ")[0]}</p>
      <div className="grid grid-cols-3 gap-2 mt-4">
        {TYPES.map(([I, t]) => <button key={t} onClick={() => setType(t)} className={`rounded-[12px] border py-2.5 flex flex-col items-center gap-1 text-[12px] transition-colors ${type === t ? "border-[color:var(--moss)] bg-[color:var(--canvas)] text-ink" : "border-line text-ink-soft"}`} data-testid={`tour-type-${t.toLowerCase()}`}><I size={16} />{t}</button>)}
      </div>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="How are they doing? (optional)" className="mt-3 w-full rounded-[12px] border border-line bg-surface p-3 text-[16px] text-ink focus:outline-none focus:border-[color:var(--moss)]" data-testid="tour-note" />
      <p className="text-[12px] text-ink-soft mt-3 mb-2">Next step</p>
      <div className="flex flex-wrap gap-2">
        {NEXT.map((n) => <button key={n} onClick={() => setNext(n)} className={`rounded-full border px-3 py-1.5 text-[12px] transition-colors ${next === n ? "border-[color:var(--deep)] bg-[color:var(--deep)] text-[color:var(--cream)]" : "border-line text-ink-soft"}`} data-testid={`tour-next-${n.toLowerCase().replace(/ /g, "-")}`}>{n}</button>)}
      </div>
      <button onClick={() => onSave(type, note.trim() || `${type === "Visit" ? "Visited" : type === "Text" ? "Texted" : "Called"} ${p.name.split(" ")[0]} to check in.`, next)} className="btn btn-primary w-full mt-5" data-testid="tour-save">Save</button>
    </div>
  );
};

const Saved = ({ p, next, onBack }) => (
  <div data-testid="tour-saved">
    <Back onClick={onBack} label="Today" />
    <div className="rounded-[12px] bg-[color:var(--deep)] text-[color:var(--cream)] p-3 flex gap-2 items-start" data-testid="tour-saved-banner">
      <Check size={16} className="mt-0.5 text-[color:var(--clay)]" /><p className="text-[13px]">Care logged for {p.name}.{next !== "No follow-up" && <> Follow-up scheduled {next.toLowerCase()}.</>}</p>
    </div>
    <Story entries={p.story} />
  </div>
);

const Phone_ = ({ children }) => (
  <div className="rounded-[38px] bg-[color:var(--deep)] p-[9px] shadow-[0_30px_70px_-25px_rgba(14,63,50,0.55)] w-full max-w-[340px] mx-auto">
    <div className="rounded-[30px] bg-paper h-[470px] sm:h-[560px] overflow-y-auto px-5 pt-6 pb-6">{children}</div>
  </div>
);

const Guide = ({ view, finished, restart }) => {
  const [n, title, body] = STEPS[view];
  return (
    <div className="flex flex-col justify-center" data-testid="tour-guide">
      <p className="t-label flex items-center gap-3"><span className="brand-rule !w-8" />Step {n} of 4</p>
      <p className="font-display font-semibold text-ink text-[26px] sm:text-[32px] leading-tight tracking-[-0.02em] mt-4" data-testid="tour-step-title">{title}</p>
      <p className="text-ink-soft text-[16px] leading-relaxed mt-3 max-w-[360px]">{body}</p>
      {finished && (
        <div className="mt-7 pt-6 border-t border-line" data-testid="tour-invite">
          <p className="text-ink text-[17px] font-semibold">That is a care moment in KNOWN.</p>
          <p className="text-ink-soft text-[15px] mt-1">Give your team one clear place to see needs and follow through.</p>
          <div className="flex flex-wrap gap-2 mt-4">
            <Link to="/sign-up" className="btn btn-primary" data-testid="tour-get-started">Get Started <ArrowRight size={16} /></Link>
            <button onClick={restart} className="btn btn-outline" data-testid="tour-restart"><RotateCcw size={15} />Start over</button>
          </div>
        </div>
      )}
      <p className="text-ink-faint text-[12px] mt-6">Sample people only. Nothing you enter is saved.</p>
    </div>
  );
};

const TourApp = () => {
  const [people, setPeople] = useState(PEOPLE);
  const [done, setDone] = useState([]);
  const [view, setView] = useState("today");
  const [pid, setPid] = useState(null);
  const [next, setNext] = useState("");
  const p = people.find((x) => x.id === pid);
  const save = (type, note, nx) => {
    const entries = [...(nx !== "No follow-up" ? [["Today", "Next step", `Follow up ${nx.toLowerCase()}`]] : []), ["Today", type, note]];
    setPeople(people.map((x) => (x.id === pid ? { ...x, story: [...entries, ...x.story] } : x)));
    setDone(done.includes(pid) ? done : [...done, pid]);
    setNext(nx); setView("story");
  };
  const restart = () => { setPeople(PEOPLE); setDone([]); setView("today"); setPid(null); };
  return (
    <div className="grid md:grid-cols-[1fr_340px] gap-8 md:gap-12 items-center">
      <Guide view={view} finished={done.length > 0} restart={restart} />
      <Phone_>
        {view === "today" && <Today people={people} done={done} open={(id) => { setPid(id); setView("person"); }} />}
        {view === "person" && <Person p={p} onBack={() => setView("today")} onLog={() => setView("log")} />}
        {view === "log" && <LogCare p={p} onBack={() => setView("person")} onSave={save} />}
        {view === "story" && <Saved p={p} next={next} onBack={() => setView("today")} />}
      </Phone_>
    </div>
  );
};

export const TourButton = ({ label = "Try a live tour", className = "mt-6", testid = "mk-hero-tour" }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} className={`inline-flex items-center gap-2 py-2.5 text-[15px] font-semibold text-[color:var(--deep)] hover:gap-3 transition-[gap] ${className}`} data-testid={testid}>
        <PlayCircle size={20} className="text-[color:var(--clay)]" />{label}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="mk max-w-[880px] w-[calc(100%-24px)] max-h-[94vh] overflow-y-auto bg-paper border-line rounded-[24px] p-6 sm:p-10" data-testid="tour-dialog">
          <DialogTitle className="sr-only">KNOWN live tour</DialogTitle>
          <DialogDescription className="sr-only">Try Today and Log Care with sample people.</DialogDescription>
          {open && <TourApp />}
        </DialogContent>
      </Dialog>
    </>
  );
};
